import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState } from "@/app/rootReducer";
import { selectIntacctId } from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { useToast } from "@/components/ui/use-toast";
import { AppDispatch } from "@/app/store";
import {
  fetchMPDashboardDataAction,
  refreshMPDashboardDataAction,
} from "../slice/mpDashboardSlice";
import { MPDashboardZipCode } from "../api/getMPDashboard/types";
import { useDownloadMPDashboardCsv } from "./useDownloadMPDashboardCsv";

export type QualificationStatus = "recommend" | "review" | "low";
export type SortField = keyof Pick<
  MPDashboardZipCode,
  | "zipcode"
  | "totalSales"
  | "totalCustomers"
  | "totalHouseholds"
  | "marketPresence"
>;
export type SortDirection = "asc" | "desc";
export type StatusFilter = "all" | QualificationStatus;

const DEFAULT_ROWS_VISIBLE = 10;

export function useMPDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const activeCompanyIntacctId = useSelector(selectIntacctId);
  const { toast } = useToast();

  const intacctId =
    searchParams.get("intacctId") || activeCompanyIntacctId || "";

  const { dashboardData, loading, refreshing, error } = useSelector(
    (state: RootState) => state.mpDashboard,
  );

  const {
    loading: csvLoading,
    downloadCsv,
    error: csvError,
  } = useDownloadMPDashboardCsv();

  const handleExportCsv = useCallback(() => {
    if (!intacctId) return;
    downloadCsv(intacctId);
  }, [intacctId, downloadCsv]);

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sortField, setSortField] = useState<SortField>("totalSales");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const loadData = useCallback(() => {
    if (!intacctId) return;
    dispatch(fetchMPDashboardDataAction(intacctId));
  }, [dispatch, intacctId]);

  const handleRefresh = useCallback(() => {
    if (!intacctId || refreshing) return;

    dispatch(refreshMPDashboardDataAction(intacctId));
  }, [dispatch, intacctId, refreshing]);

  useEffect(() => {
    if (!hasInitiallyLoaded && intacctId) {
      loadData();
      setHasInitiallyLoaded(true);
    }
  }, [hasInitiallyLoaded, intacctId, loadData]);

  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error,
      });
    }
  }, [error, toast]);

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("desc");
      }
    },
    [sortField],
  );

  const items = dashboardData.items;
  const summary = dashboardData.summary;
  const lastSync = dashboardData.lastSync;

  const statusCounts = useMemo(() => {
    const counts = { all: items.length, recommend: 0, review: 0, low: 0 };
    items.forEach((item) => {
      const status = item.qualificationStatus as QualificationStatus;
      if (status in counts) {
        counts[status]++;
      }
    });
    return counts;
  }, [items]);

  const filteredAndSorted = useMemo(() => {
    let result = items;

    if (statusFilter !== "all") {
      result = result.filter(
        (item) => item.qualificationStatus === statusFilter,
      );
    }

    result = [...result].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const multiplier = sortDirection === "asc" ? 1 : -1;
      if (typeof aVal === "string" && typeof bVal === "string") {
        return aVal.localeCompare(bVal) * multiplier;
      }
      return ((aVal as number) - (bVal as number)) * multiplier;
    });

    return result;
  }, [items, statusFilter, sortField, sortDirection]);

  const filteredSummary = useMemo(() => {
    if (statusFilter === "all") return summary;

    const filtered = filteredAndSorted;
    return {
      totalSales: filtered.reduce((sum, item) => sum + item.totalSales, 0),
      totalCustomers: filtered.reduce(
        (sum, item) => sum + item.totalCustomers,
        0,
      ),
      qualifiedZipCount: filtered.filter(
        (item) => item.qualificationStatus === "recommend",
      ).length,
    };
  }, [summary, statusFilter, filteredAndSorted]);

  const visibleRows = expanded
    ? filteredAndSorted
    : filteredAndSorted.slice(0, DEFAULT_ROWS_VISIBLE);

  const canExpand = filteredAndSorted.length > DEFAULT_ROWS_VISIBLE;

  return {
    loading,
    refreshing,
    items: visibleRows,
    totalFilteredCount: filteredAndSorted.length,
    summary: filteredSummary,
    expanded,
    setExpanded,
    canExpand,
    sortField,
    sortDirection,
    handleSort,
    statusFilter,
    setStatusFilter,
    statusCounts,
    totalZipCount: items.length,
    lastSync,
    handleRefresh,
    csvLoading,
    csvError,
    handleExportCsv,
  };
}
